"use client";

import { useState } from "react";
import Link from "next/link";

interface FAQ {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  faqs: FAQ[];
}

const faqCategories: FAQCategory[] = [
  {
    title: "Getting Started",
    faqs: [
      {
        question: "How do I create an account?",
        answer:
          "Visit our registration page and sign up with your email address. You can also sign up using Google. No credit card is required for the free plan. Once registered, you'll be guided through a quick onboarding to set up your team and connect your first marketing channel.",
      },
      {
        question: "How does the AI team work?",
        answer:
          "8thFloor gives you a team of 8 specialized AI marketing agents: a Strategist, Copywriter, Art Director, Media Planner, Analyst, SEO Specialist, Social Media Manager, and Growth Hacker. Each agent has deep expertise in their area and they work together to plan, execute, and optimize your marketing campaigns.",
      },
      {
        question: "What's included in the free plan?",
        answer:
          "The free plan includes access to all 8 AI agents with limited usage, basic email marketing tools, social media scheduling for one account, a marketing dashboard with core analytics, and up to 500 subscribers. It's a great way to explore the platform before upgrading.",
      },
    ],
  },
  {
    title: "Email Marketing",
    faqs: [
      {
        question: "How do I send my first campaign?",
        answer:
          "Navigate to the Email section from your dashboard. Click 'New Campaign,' choose a template or start from scratch, and use our AI copywriter to help draft your content. Add your subject line, select your audience, and preview before sending. You can also schedule campaigns for later.",
      },
      {
        question: "How does email tracking work?",
        answer:
          "We automatically track opens, clicks, bounces, and unsubscribes for every campaign. View detailed analytics including open rates, click-through rates, and engagement heatmaps in the Email Analytics section. All tracking is compliant with privacy regulations.",
      },
      {
        question: "Can I import my existing subscribers?",
        answer:
          "Yes! You can import subscribers via CSV file upload. Go to Email > Subscribers > Import and upload your file. We'll map the columns automatically and handle deduplication. Make sure you have permission to email all imported contacts to comply with anti-spam laws.",
      },
    ],
  },
  {
    title: "Social Media",
    faqs: [
      {
        question: "How do I connect my social accounts?",
        answer:
          "Go to Settings > Integrations and click 'Connect' next to the social platform you want to add. We support Facebook, Instagram, X (Twitter), LinkedIn, and more. You'll be redirected to authorize 8thFloor to manage posts on your behalf.",
      },
      {
        question: "Can I schedule posts in advance?",
        answer:
          "Absolutely. Our social media tools include a full content calendar where you can schedule posts days, weeks, or months ahead. Use our AI Social Media Manager to generate a week's worth of content, then review and schedule it all at once.",
      },
    ],
  },
  {
    title: "AI Features",
    faqs: [
      {
        question: "What does the command center show me?",
        answer:
          "The analytics hub pulls every connected channel — ads, email, social, web, and CRM — into one view, with prior-period comparisons so you can see what's trending. An AI analyst reads the numbers and tells you, in plain language, what's working and what to do next. If a channel hasn't synced, it says so, so you never mistake a gap for a zero.",
      },
      {
        question: "What is the Campaign Generator?",
        answer:
          "The Campaign Generator uses your AI team to create a full marketing campaign from a single brief. Describe your goal, audience, and budget, and the AI agents collaborate to produce a strategy, copy, channel recommendations, budget allocation, and a timeline — all in minutes.",
      },
      {
        question: "How does Brand Voice DNA work?",
        answer:
          "Brand Voice DNA analyzes your existing content (website, emails, social posts) to extract your unique brand voice profile. It captures your tone, vocabulary, sentence patterns, and personality traits, then uses this profile to ensure all AI-generated content sounds authentically like your brand.",
      },
    ],
  },
  {
    title: "Billing",
    faqs: [
      {
        question: "How do I upgrade my plan?",
        answer:
          "Go to Settings > Billing and click 'Upgrade.' Choose your new plan (Starter, Pro, or Enterprise) and enter your payment details. Upgrades take effect immediately, and you'll be charged a prorated amount for the remainder of your current billing cycle.",
      },
      {
        question: "Can I cancel anytime?",
        answer:
          "Yes. You can cancel your subscription at any time from Settings > Billing. Your account will remain active until the end of your current billing period. After cancellation, your account will revert to the free plan and you'll retain access to your data.",
      },
      {
        question: "Do you offer refunds?",
        answer:
          "We offer a full refund within the first 14 days of any paid subscription if you're not satisfied. After 14 days, we do not provide prorated refunds for cancellations. Contact support@8thfloor.ai for refund requests.",
      },
    ],
  },
  {
    title: "Technical",
    faqs: [
      {
        question: "Is my data secure?",
        answer:
          "Yes. We use industry-standard encryption for data at rest and in transit (TLS 1.2+). Our infrastructure runs on AWS with regular security audits. Passwords are hashed and salted. We never share your data with third parties except as required to provide the service (see our Privacy Policy).",
      },
      {
        question: "What AI model do you use?",
        answer:
          "8thFloor is powered by Anthropic's Claude, one of the most capable and safety-focused AI models available. Each of our 8 agents uses specialized prompts and configurations optimized for their marketing domain.",
      },
      {
        question: "Can I export my data?",
        answer:
          "Yes. You can export your subscriber lists, campaign analytics, content, and other data from Settings > Data Export. We provide exports in standard formats (CSV, JSON) so you can take your data with you at any time.",
      },
    ],
  },
];

export default function HelpCenterPage() {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCategories = faqCategories
    .map((category) => ({
      ...category,
      faqs: category.faqs.filter(
        (faq) =>
          searchQuery === "" ||
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter((category) => category.faqs.length > 0);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <Link href="/home" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-bold text-sm">
              8F
            </div>
          </Link>
          <Link
            href="/home"
            className="text-sm text-slate-500 hover:text-slate-900 transition-colors"
          >
            &larr; Back to Home
          </Link>
        </div>
      </header>

      {/* Hero + Search */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 py-20 sm:py-28">
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-white/5 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-indigo-400/10 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
            Help Center
          </h1>
          <p className="mt-4 text-lg text-indigo-200">
            Find answers to common questions about 8thFloor.
          </p>
          <div className="mt-8 max-w-xl mx-auto">
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-5 py-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder:text-indigo-300 focus:outline-none focus:ring-2 focus:ring-white/50 text-base"
            />
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-lg text-slate-500">
                No results found for &ldquo;{searchQuery}&rdquo;. Try a
                different search term.
              </p>
            </div>
          ) : (
            <div className="space-y-12">
              {filteredCategories.map((category) => (
                <div key={category.title}>
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">
                    {category.title}
                  </h2>
                  <div className="space-y-3">
                    {category.faqs.map((faq) => (
                      <details
                        key={faq.question}
                        className="group border border-slate-200 rounded-xl overflow-hidden"
                      >
                        <summary className="flex items-center justify-between cursor-pointer px-6 py-4 text-left font-medium text-slate-900 hover:bg-slate-50 transition-colors">
                          <span>{faq.question}</span>
                          <span className="ml-4 flex-shrink-0 text-slate-400 group-open:rotate-180 transition-transform">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                            >
                              <path
                                d="M5 7.5L10 12.5L15 7.5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </summary>
                        <div className="px-6 pb-4 text-slate-600 leading-relaxed">
                          {faq.answer}
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-20 sm:py-28 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Still need help?
          </h2>
          <p className="mt-4 text-slate-500 max-w-xl mx-auto">
            Our team is here to support you. Reach out and we&rsquo;ll get back
            to you as soon as possible.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/contact"
              className="inline-flex items-center px-6 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 font-semibold shadow-sm hover:shadow-md transition-all"
            >
              Contact Form
            </a>
            <a
              href="mailto:support@8thfloor.ai"
              className="inline-flex items-center px-6 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 font-semibold shadow-sm hover:shadow-md transition-all"
            >
              Email support@8thfloor.ai
            </a>
          </div>
          <div className="mt-10 pt-10 border-t border-slate-200">
            <p className="text-slate-500 mb-4">
              Or ask Maya, your AI strategist
            </p>
            <Link
              href="/ai?agent=strategist"
              className="inline-flex items-center px-8 py-4 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-bold text-lg shadow-xl shadow-indigo-500/25 hover:shadow-indigo-500/40 hover:scale-[1.02] transition-all"
            >
              Chat with Maya
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <p className="text-sm text-slate-500">
            &copy; 2026 Neo-2 Consulting. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
